import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Shield, Car, AlertTriangle, DollarSign, Save, Send, 
  FileText, Wrench, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Import sub-components
import { 
  DamagedPartsList, 
  ValuationCostSummary,
  InsuranceInfoSection,
  IncidentInfoSection,
  NotesSection
} from './damage-valuation';

// Section Header Component
const SectionHeader = ({ title, icon: Icon, section, badge, expanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
  >
    <div className="flex items-center space-x-2">
      <Icon className="w-5 h-5 text-purple-600" />
      <span className="font-medium text-slate-900">{title}</span>
      {badge && <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">{badge}</Badge>}
    </div>
    {expanded ? (
      <ChevronUp className="w-5 h-5 text-slate-500" />
    ) : (
      <ChevronDown className="w-5 h-5 text-slate-500" />
    )}
  </button>
);

// Initial form state
const getInitialFormData = (serviceRequest) => ({
  insurance_company: serviceRequest?.insurance_company || '',
  policy_number: serviceRequest?.policy_number || '',
  claim_number: serviceRequest?.claim_number || '',
  deductible: '',
  agency_code: '',
  incident_date: '',
  incident_cause: '',
  incident_description: serviceRequest?.description || '',
  incident_location: '',
  labor_cost: 0,
  paint_cost: 0,
  other_costs: 0,
  tax_rate: 13,
  notes: '',
  recommendations: '',
  estimated_repair_days: ''
});

const DamageValuationForm = ({ 
  isOpen, 
  onClose, 
  serviceRequest, 
  existingValuation = null,
  onSaved 
}) => {
  const { API, sessionToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    insurance: true,
    incident: true,
    parts: true,
    costs: true,
    notes: false
  });

  const [formData, setFormData] = useState(() => getInitialFormData(serviceRequest));
  const [damagedParts, setDamagedParts] = useState([]);

  // Load existing data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingValuation) {
        loadExistingData(existingValuation);
      } else if (serviceRequest) {
        setFormData(getInitialFormData(serviceRequest));
        setDamagedParts([]);
      }
    }
  }, [isOpen, existingValuation, serviceRequest]);

  const loadExistingData = useCallback((valuation) => {
    setFormData({
      insurance_company: valuation.insurance_company || '',
      policy_number: valuation.policy_number || '',
      claim_number: valuation.claim_number || '',
      deductible: valuation.deductible || '',
      agency_code: valuation.agency_code || '',
      incident_date: valuation.incident_date ? valuation.incident_date.split('T')[0] : '',
      incident_cause: valuation.incident_cause || '',
      incident_description: valuation.incident_description || '',
      incident_location: valuation.incident_location || '',
      labor_cost: valuation.labor_cost || 0,
      paint_cost: valuation.paint_cost || 0,
      other_costs: valuation.other_costs || 0,
      tax_rate: valuation.tax_rate || 13,
      notes: valuation.notes || '',
      recommendations: valuation.recommendations || '',
      estimated_repair_days: valuation.estimated_repair_days || ''
    });
    setDamagedParts(valuation.damaged_parts || []);
  }, []);

  // Form change handler
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Damaged parts handlers
  const handleAddPart = useCallback((partName) => {
    if (damagedParts.some(p => p.part_name === partName)) {
      toast.error('Esta parte ya está agregada');
      return;
    }
    setDamagedParts(prev => [...prev, {
      part_name: partName,
      severity: 'moderado',
      description: '',
      estimated_cost: 0,
      requires_replacement: false,
      photo_urls: []
    }]);
  }, [damagedParts]);

  const handleUpdatePart = useCallback((index, field, value) => {
    setDamagedParts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleRemovePart = useCallback((index) => {
    setDamagedParts(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Toggle section
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Save handler
  const handleSave = useCallback(async (submit = false) => {
    if (!formData.insurance_company) {
      toast.error('Selecciona la aseguradora');
      return;
    }
    if (!formData.incident_description) {
      toast.error('Describe el incidente');
      return;
    }
    if (submit && damagedParts.length === 0) {
      toast.error('Agrega al menos una parte dañada');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        service_request_id: serviceRequest.id,
        ...formData,
        damaged_parts: damagedParts
      };

      let response;
      if (existingValuation) {
        response = await axios.put(
          `${API}/damage-valuations/${existingValuation.id}`,
          payload,
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      } else {
        response = await axios.post(
          `${API}/damage-valuations`,
          payload,
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      }

      if (submit) {
        const valuationId = response.data.id || existingValuation?.id;
        await axios.post(
          `${API}/damage-valuations/${valuationId}/submit`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
        toast.success('Valuación enviada para revisión');
      } else {
        toast.success('Valuación guardada como borrador');
      }

      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving valuation:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  }, [formData, damagedParts, serviceRequest, existingValuation, API, sessionToken, onSaved, onClose]);

  // Status badge
  const statusBadge = useMemo(() => {
    if (!existingValuation) return null;
    const statusMap = {
      draft: { class: 'bg-slate-100 text-slate-700', label: 'Borrador' },
      submitted: { class: 'bg-blue-100 text-blue-700', label: 'Enviada' },
      approved: { class: 'bg-green-100 text-green-700', label: 'Aprobada' },
      rejected: { class: 'bg-red-100 text-red-700', label: 'Rechazada' },
      requires_revision: { class: 'bg-yellow-100 text-yellow-700', label: 'Requiere Revisión' }
    };
    const status = statusMap[existingValuation.status] || statusMap.draft;
    return <Badge className={status.class}>{status.label}</Badge>;
  }, [existingValuation]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <span>Valuación de Daños</span>
            {statusBadge}
          </DialogTitle>
        </DialogHeader>

        {/* Vehicle Info Summary */}
        {serviceRequest && (
          <Card className="p-4 bg-purple-50 border-purple-200 mb-4">
            <div className="flex items-center space-x-3">
              <Car className="w-8 h-8 text-purple-600" />
              <div>
                <p className="font-semibold text-slate-900">
                  {serviceRequest.vehicle_info?.make} {serviceRequest.vehicle_info?.model} {serviceRequest.vehicle_info?.year}
                </p>
                <p className="text-sm text-slate-600">
                  Placa: {serviceRequest.vehicle_info?.license_plate} | 
                  Servicio: {serviceRequest.service_type}
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-4">
          {/* Insurance Information */}
          <div className="border rounded-lg overflow-hidden">
            <SectionHeader 
              title="Información del Seguro" 
              icon={Shield} 
              section="insurance"
              expanded={expandedSections.insurance}
              onToggle={() => toggleSection('insurance')}
            />
            {expandedSections.insurance && (
              <InsuranceInfoSection formData={formData} onFormChange={handleFormChange} />
            )}
          </div>

          {/* Incident Information */}
          <div className="border rounded-lg overflow-hidden">
            <SectionHeader 
              title="Información del Incidente" 
              icon={AlertTriangle} 
              section="incident"
              expanded={expandedSections.incident}
              onToggle={() => toggleSection('incident')}
            />
            {expandedSections.incident && (
              <IncidentInfoSection formData={formData} onFormChange={handleFormChange} />
            )}
          </div>

          {/* Damaged Parts */}
          <div className="border rounded-lg overflow-hidden">
            <SectionHeader 
              title="Partes Dañadas" 
              icon={Wrench} 
              section="parts"
              badge={`${damagedParts.length} partes`}
              expanded={expandedSections.parts}
              onToggle={() => toggleSection('parts')}
            />
            {expandedSections.parts && (
              <div className="p-4">
                <DamagedPartsList
                  damagedParts={damagedParts}
                  onAddPart={handleAddPart}
                  onUpdatePart={handleUpdatePart}
                  onRemovePart={handleRemovePart}
                />
              </div>
            )}
          </div>

          {/* Costs */}
          <div className="border rounded-lg overflow-hidden">
            <SectionHeader 
              title="Costos y Totales" 
              icon={DollarSign} 
              section="costs"
              expanded={expandedSections.costs}
              onToggle={() => toggleSection('costs')}
            />
            {expandedSections.costs && (
              <ValuationCostSummary 
                damagedParts={damagedParts}
                formData={formData}
                onFormChange={handleFormChange}
              />
            )}
          </div>

          {/* Notes & Recommendations */}
          <div className="border rounded-lg overflow-hidden">
            <SectionHeader 
              title="Notas y Recomendaciones" 
              icon={FileText} 
              section="notes"
              expanded={expandedSections.notes}
              onToggle={() => toggleSection('notes')}
            />
            {expandedSections.notes && (
              <NotesSection formData={formData} onFormChange={handleFormChange} />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={loading}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar Borrador
            </Button>
            <Button
              type="button"
              onClick={() => handleSave(true)}
              disabled={loading || damagedParts.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-valuation-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Enviar a Aseguradora
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DamageValuationForm;
